import { css } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { Avatar, SelectItemProps } from '@mantine/core';
import { Form } from '@rr/ui';
import { getRandomThemeColor } from '@rr/utils';

import { AUTOCOMPLETE_STUDENTS_QUERY } from './AutocompleteStudent.query.gql';
import { AutocompleteStudentsQuery } from 'types/graphql';
import { forwardRef } from 'react';

interface AutocompleteStudentProps {
  label: string;
  name: string;
  value?: {
    value: string;
    label: string;
  }[];
  multiple?: boolean;
}

interface ItemProps extends SelectItemProps {
  firstName: string;
  lastName: string;
}

const randomColor = getRandomThemeColor();

const AutocompleteItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ value, firstName, lastName, ...rest }: ItemProps, ref) => {
    const styles = css`
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 8px;
    `;

    return (
      <div css={styles} ref={ref} {...rest}>
        <Avatar color="blue" radius="xl" size="sm">
          {firstName.charAt(0)}
          {lastName.charAt(0)}
        </Avatar>
        <div>
          {firstName} {lastName}
        </div>
      </div>
    );
  }
);

export const AutocompleteStudent = ({
  label,
  value,
  multiple,
  ...props
}: AutocompleteStudentProps) => {
  const { data, error, loading } = useQuery<AutocompleteStudentsQuery>(
    AUTOCOMPLETE_STUDENTS_QUERY
  );
  if (error) {
    console.error(error);
    return <div>Error! Please check the console.</div>;
  }

  const students = data?.students?.map((student) => ({
    value: student.id,
    label: `${student.first_name} ${student.last_name}`,
    firstName: student.first_name,
    lastName: student.last_name,
  })) ?? ['- No data provided -'];

  return multiple ? (
    <Form.MultiSelect
      label={label}
      itemComponent={AutocompleteItem}
      data={value ?? students}
      disabled={loading}
      searchable
      {...props}
    />
  ) : (
    <Form.ACField
      label={label}
      itemComponent={AutocompleteItem}
      data={value ?? students}
      disabled={loading}
      searchable
      {...props}
    />
  );
};
